// The command palette (spec/13 §2's `Ctrl+K`, and the design guide's "command palette is table
// stakes"): navigation turned into search, plus a few actions. It is a real dialog, not a search box
// — the kit's `Dialog` (Radix) owns the focus trap, the return of focus to whatever opened it,
// Escape, and the inert background; this component owns the combobox + listbox on top, with up/down
// to move the active row and Enter to run it (WAI-ARIA's combobox-with-listbox pattern).
//
// The header the kit's Dialog draws (its title and close button) is hidden by CSS: a palette closes
// on Escape, on a click outside, or on a selection, and its title is only there to name the dialog
// for a screen reader (`.console-palette` in styles.css).

import { useTranslation } from '@clavian/i18n/react';
import { Dialog, Kbd, cx } from '@clavian/ui-web';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { SearchIcon } from './nav-icons';

export interface PaletteItem {
  readonly id: string;
  readonly label: string;
  readonly icon: ReactNode;
  /** A hint at the end of the row — a keyboard shortcut, or "Soon". */
  readonly hint?: string;
  /** A second, quieter line under the label: where a setting is ("Payments · Organisation"). */
  readonly detail?: string;
  /**
   * Run once the palette has closed and let go of focus, rather than as it closes: for an action that opens another
   * layer (the venue switcher), which focus coming back to the search trigger would otherwise dismiss.
   */
  readonly afterClose?: boolean;
  readonly run: () => void;
}

export interface PaletteSection {
  readonly id: string;
  readonly label: string;
  readonly items: readonly PaletteItem[];
}

export interface CommandPaletteProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly sections: readonly PaletteSection[];
  /**
   * Results that come from the query rather than from a fixed list — the settings search
   * (`settings/search.ts`): already matched and ranked, shown after "Go to" and never filtered again.
   */
  readonly search?: (query: string) => readonly PaletteSection[];
}

export function CommandPalette({ open, onOpenChange, sections, search }: CommandPaletteProps): ReactNode {
  const { t } = useTranslation('console');
  // An `afterClose` item chosen: run when the dialog hands focus back, in place of handing it back.
  const pending = useRef<(() => void) | null>(null);
  const close = useCallback(
    (then?: () => void) => {
      pending.current = then ?? null;
      onOpenChange(false);
    },
    [onOpenChange],
  );
  return (
    <Dialog
      title={t('palette.title')}
      description={t('palette.hint')}
      open={open}
      onOpenChange={onOpenChange}
      onCloseAutoFocus={(event) => {
        const then = pending.current;
        if (then === null) return;
        pending.current = null;
        event.preventDefault();
        then();
      }}
      className="console-palette"
    >
      {/* Remounted on every open (Radix unmounts closed content), so the query and the active row
          start fresh each time. */}
      {open ? <PaletteBody sections={sections} {...(search === undefined ? {} : { search })} onClose={close} /> : null}
    </Dialog>
  );
}

function PaletteBody({
  sections,
  search,
  onClose,
}: {
  readonly sections: readonly PaletteSection[];
  readonly search?: (query: string) => readonly PaletteSection[];
  /** Close the palette, and run `then` once it has let go of focus. */
  readonly onClose: (then?: () => void) => void;
}) {
  const { t } = useTranslation('console');
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Put the caret in the search field the moment the palette opens, over the dialog's default focus.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Filter by the query, and hand every visible item its flat index across the groups — computed
  // without mutation so a render never reassigns a variable after it completes.
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matched = sections
      .map((section) => ({
        id: section.id,
        label: section.label,
        items: needle === '' ? section.items : section.items.filter((item) => item.label.toLowerCase().includes(needle)),
      }))
      .filter((section) => section.items.length > 0);
    // The query's own results (settings) follow the sections you can go to, before the actions.
    const found = needle === '' || search === undefined ? [] : search(query).filter((section) => section.items.length > 0);
    const goTo = matched.filter((section) => section.id === 'go-to');
    const filtered = [...goTo, ...found, ...matched.filter((section) => section.id !== 'go-to')];
    const withIndices = filtered.reduce<{ readonly groups: readonly IndexedGroup[]; readonly total: number }>(
      (acc, section) => ({
        groups: [...acc.groups, { id: section.id, label: section.label, items: section.items.map((item, i) => ({ item, index: acc.total + i })) }],
        total: acc.total + section.items.length,
      }),
      { groups: [], total: 0 },
    );
    const flat = filtered.flatMap((section) => section.items);
    return { groups: withIndices.groups, flat, count: withIndices.total };
  }, [sections, search, query]);

  const { count } = shown;
  // The stored index may point past the list once the query shrinks it; wrap it into range on read,
  // rather than correcting state in an effect.
  const current = count === 0 ? -1 : ((active % count) + count) % count;

  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [current]);

  const run = (item: PaletteItem | undefined) => {
    if (item === undefined) return;
    if (item.afterClose === true) {
      onClose(item.run);
      return;
    }
    onClose();
    item.run();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (count === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(current + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(current - 1 + count);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(count - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      run(shown.flat[current]);
    }
    // Escape is the Dialog's: it closes and returns focus to the opener.
  };

  const activeId = count > 0 ? `console-palette-opt-${current}` : undefined;

  return (
    <div className="console-palette__box">
      <div className="console-palette__search">
        <span className="console-palette__search-icon">
          <SearchIcon />
        </span>
        <input
          ref={inputRef}
          className="console-palette__input"
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls="console-palette-list"
          aria-activedescendant={activeId}
          aria-label={t('palette.placeholder')}
          placeholder={t('palette.placeholder')}
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      <div className="console-palette__list" id="console-palette-list" role="listbox" aria-label={t('palette.results')} ref={listRef}>
        {shown.groups.map((section) => (
          <div className="console-palette__group" role="group" aria-label={section.label} key={section.id}>
            <div className="console-palette__group-label" aria-hidden="true">
              {section.label}
            </div>
            {section.items.map(({ item, index }) => (
              // A button, not a div, carries the option: it is interactive by nature, so it needs no
              // hand-added key handler, and `role="option"` is a role HTML allows a button to take.
              // `tabIndex={-1}` keeps focus on the input — the listbox is driven by aria-activedescendant.
              <button
                key={item.id}
                type="button"
                id={`console-palette-opt-${index}`}
                role="option"
                aria-selected={index === current}
                tabIndex={-1}
                className={cx('console-palette__row', index === current && 'is-active')}
                onClick={() => run(item)}
                onPointerMove={() => setActive(index)}
              >
                <span className="console-palette__row-icon">{item.icon}</span>
                {item.detail === undefined ? (
                  <span className="console-palette__row-label">{item.label}</span>
                ) : (
                  <span className="console-palette__row-text">
                    <span className="console-palette__row-label">{item.label}</span>
                    <span className="console-palette__row-detail">{item.detail}</span>
                  </span>
                )}
                {item.hint === undefined ? null : <span className="console-palette__row-hint">{item.hint}</span>}
              </button>
            ))}
          </div>
        ))}
        {count === 0 ? (
          // A non-actionable option keeps the listbox owning an option (valid ARIA) while it is empty.
          <div className="console-palette__empty" role="option" aria-disabled="true" aria-selected={false}>
            {t('palette.empty', { query: query.trim() })}
          </div>
        ) : null}
      </div>
      <div className="console-palette__foot" aria-hidden="true">
        <span className="console-palette__foot-item">
          <Kbd keys={['↑', '↓']} />
          {t('palette.footMove')}
        </span>
        <span className="console-palette__foot-item">
          <Kbd keys={['Enter']} />
          {t('palette.footOpen')}
        </span>
        <span className="console-palette__foot-item">
          <Kbd keys={['Esc']} />
          {t('palette.footClose')}
        </span>
      </div>
    </div>
  );
}

interface IndexedItem {
  readonly item: PaletteItem;
  readonly index: number;
}
interface IndexedGroup {
  readonly id: string;
  readonly label: string;
  readonly items: readonly IndexedItem[];
}
